import { useContext, useEffect, useState } from "react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { getDatesByYear } from "../core/api";
import { ALLOW_DOWNLOADS, AVAILABLE_YEARS, KHIDMAT } from "../core/constant";
import { CoordinatorContext } from "../context/CoordinatorContext";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#D084D0", "#8884D8", "#FFC658", "#FF7C7C", "#8DD1E1", "#87D068", "#FFA07A", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9", "#F8C471", "#82E0AA", "#F1948A", "#AED6F1"];

const Dashboard = () => {
  const { coordinator } = useContext(CoordinatorContext);
  const [volunteerData, setVolunteerData] = useState({ total: 0, daily: [], serviceDist: [], venueDist: [], volunteersList: [], ageServiceDist: [] });

  // Separate function to analyze unique volunteers by age group for each venue
  const analyzeVenueAgeDistribution = (venueUniqueVolunteers, volunteersList) => {
    // Initialize venue age analysis structure
    const venueAgeAnalysis = {};
    Object.entries(venueUniqueVolunteers).forEach(([venue, volunteerIds]) => {
      venueAgeAnalysis[venue] = {
        '11-14': new Set(),
        '14-25': new Set(),
        '25-45': new Set(),
        '45+': new Set(),
        'Unknown': new Set()
      };
    });

    // Go through all volunteers and categorize them by venue and age
    volunteersList.forEach(volunteer => {
      const venue = volunteer.venue;
      const age = volunteer.age || 0;
      const itsNo = volunteer.ITSNo;

      if (venueAgeAnalysis[venue]) {
        if (age >= 11 && age < 14) {
          venueAgeAnalysis[venue]['11-14'].add(itsNo);
        } else if (age >= 14 && age < 25) {
          venueAgeAnalysis[venue]['14-25'].add(itsNo);
        } else if (age >= 25 && age < 45) {
          venueAgeAnalysis[venue]['25-45'].add(itsNo);
        } else if (age >= 45) {
          venueAgeAnalysis[venue]['45+'].add(itsNo);
        } else {
          venueAgeAnalysis[venue]['Unknown'].add(itsNo);
        }
      }
    });

    // Convert Sets to counts and console log the results
    const venueAgeReport = {};
    Object.entries(venueAgeAnalysis).forEach(([venue, ageGroups]) => {
      venueAgeReport[venue] = {
        '11-14': ageGroups['11-14'].size,
        '14-25': ageGroups['14-25'].size,
        '25-45': ageGroups['25-45'].size,
        '45+': ageGroups['45+'].size,
        'Unknown': ageGroups['Unknown'].size,
        total: Object.values(ageGroups).reduce((sum, set) => sum + set.size, 0)
      };
    });

    console.log('=== UNIQUE VOLUNTEERS BY VENUE AND AGE GROUP ===');
    Object.entries(venueAgeReport).forEach(([venue, ageData]) => {
      console.log(`\n📍 VENUE: ${venue}`);
      console.log(`   Total Unique Volunteers: ${ageData.total}`);
      console.log(`   Age 11-14: ${ageData['11-14']} volunteers`);
      console.log(`   Age 14-25: ${ageData['14-25']} volunteers`);
      console.log(`   Age 25-45: ${ageData['25-45']} volunteers`);
      console.log(`   Age 45+: ${ageData['45+']} volunteers`);
      if (ageData['Unknown'] > 0) {
        console.log(`   Unknown Age: ${ageData['Unknown']} volunteers`);
      }
    });
    console.log('=== END OF VENUE AGE ANALYSIS ===\n');
  };
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState(AVAILABLE_YEARS);
  const [searchTerm, setSearchTerm] = useState('');

  const allowDownload = ALLOW_DOWNLOADS.includes(coordinator?.email);
  const processVolunteerData = (data) => {
    let totalVolunteers = new Set();
    let daily = [];
    let volunteersList = [];

    let serviceUniqueVolunteers = {}; // Track unique volunteers per service
    let venueUniqueVolunteers = {}; // Track unique volunteers per venue
    let venueServiceVolunteers = {}; // Track unique volunteers per venue-service combination
    let serviceAgeDistribution = {}; // Track age distribution per service
    let presentVolunteers = new Set();
    let attendees = 0;
    let dailyUniqueVolunteers = new Set(); // Track unique volunteers across all days
    let venueUniqueVolunteersTracker = new Set(); // Track unique volunteers across all venues
    let cumulativeVolunteers = new Set(); // Track volunteers seen in previous days

    data?.forEach((details) => {
      let dailyVolunteers = new Set(); // Track unique volunteers for each day
      let dailyPresentVolunteers = new Set(); // Track unique present volunteers for each day
      let newVolunteersToday = new Set(); // Track NEW volunteers for this day only
      let presentCount = 0;
      Object.entries(details.venues).forEach(([venue, venueData]) => {
        if (!venueUniqueVolunteers[venue]) {
          venueUniqueVolunteers[venue] = new Set();
        }
        if (!venueServiceVolunteers[venue]) {
          venueServiceVolunteers[venue] = {};
        }

        Object.entries(venueData.services).forEach(([service, serviceData]) => {
          if (!serviceUniqueVolunteers[service]) {
            serviceUniqueVolunteers[service] = new Set();
          }
          if (!venueServiceVolunteers[venue][service]) {
            venueServiceVolunteers[venue][service] = new Set();
          }
          if (!serviceAgeDistribution[service]) {
            serviceAgeDistribution[service] = { '11-14': 0, '14-25': 0, '25-45': 0, '45+': 0 };
          }

          serviceData?.volunteers?.forEach((volunteer) => {
            totalVolunteers.add(volunteer.ITSNo);
            dailyVolunteers.add(volunteer.ITSNo);
            dailyUniqueVolunteers.add(volunteer.ITSNo);
            venueUniqueVolunteers[venue].add(volunteer.ITSNo);
            venueUniqueVolunteersTracker.add(volunteer.ITSNo);
            serviceUniqueVolunteers[service].add(volunteer.ITSNo);
            venueServiceVolunteers[venue][service].add(volunteer.ITSNo);

            // Track new volunteers (not seen in previous days)
            if (!cumulativeVolunteers.has(volunteer.ITSNo)) {
              newVolunteersToday.add(volunteer.ITSNo);
            }

            // Age distribution logic
            const age = volunteer.age || 0;
            if (age >= 11 && age < 14) {
              serviceAgeDistribution[service]['11-14']++;
            } else if (age >= 14 && age < 25) {
              serviceAgeDistribution[service]['14-25']++;
            } else if (age >= 25 && age < 45) {
              serviceAgeDistribution[service]['25-45']++;
            } else if (age >= 45) {
              serviceAgeDistribution[service]['45+']++;
            }

            if (volunteer.status === 'PRESENT') {
              presentVolunteers.add(volunteer.ITSNo);
              dailyPresentVolunteers.add(volunteer.ITSNo);
              attendees++;
              presentCount++;
            }

            volunteersList.push({
              date: details.id,
              venue,
              service: KHIDMAT[service],
              name: volunteer.name,
              id: volunteer.ITSNo,
              phone: volunteer.phone,
              status: volunteer.status,
              ITSNo: volunteer.ITSNo,
              age: volunteer.age,
            });
          });
        });
      });

      // Add all volunteers from today to cumulative set for future days
      dailyVolunteers.forEach(volunteer => cumulativeVolunteers.add(volunteer));

      daily.push({
        date: details.id,
        count: dailyVolunteers.size, // Total unique volunteers for this day
        newVolunteers: newVolunteersToday.size, // NEW volunteers for this day
        presentCount: dailyPresentVolunteers.size,
        totalRegistrations: Object.values(details.venues).reduce((acc, venue) => {
          return acc + Object.values(venue.services).reduce((serviceAcc, service) => {
            return serviceAcc + (service.volunteers?.length || 0);
          }, 0);
        }, 0)
      });
    });

    // Process age-wise distribution for charts
    const ageServiceDist = [];
    Object.entries(serviceAgeDistribution).forEach(([service, ageGroups]) => {
      const serviceData = {
        service,
        '11-14': ageGroups['11-14'],
        '14-25': ageGroups['14-25'],
        '25-45': ageGroups['25-45'],
        '45+': ageGroups['45+'],
      };
      ageServiceDist.push(serviceData);
    });

    // Get all unique services for venue distribution
    const allServices = [...new Set(Object.keys(serviceUniqueVolunteers))];

    // Process venue-service distribution for grouped chart
    const venueServiceDist = Object.entries(venueServiceVolunteers).map(([venue, services]) => {
      const venueData = { venue };
      allServices.forEach(service => {
        venueData[service] = services[service] ? services[service].size : 0;
      });
      return venueData;
    });


    // Call the venue age analysis function (comment/uncomment as needed)
   // analyzeVenueAgeDistribution(venueUniqueVolunteers, volunteersList);

    // Convert unique counts for service and venue distributions
    setVolunteerData({
      total: totalVolunteers.size, // Unique total volunteers
      daily,
      serviceDist: Object.entries(serviceUniqueVolunteers).map(([service, ids]) => ({
        service,
        count: ids.size,
      })),
      venueDist: Object.entries(venueUniqueVolunteers).map(([venue, ids]) => ({
        venue,
        count: ids.size,
        uniqueVolunteers: ids.size,
      })),
      venueServiceDist,
      allServices,
      volunteersList,
      presentees: presentVolunteers.size,
      attendees: attendees,
      ageServiceDist,
      uniqueVolunteersAcrossAllDays: dailyUniqueVolunteers.size,
      uniqueVolunteersAcrossAllVenues: venueUniqueVolunteersTracker.size,
    });
  };

  const fetchDateData = async (year) => {
    const datelist = await getDatesByYear(year);
    processVolunteerData(datelist);
  };

  useEffect(() => {
    fetchDateData(selectedYear);
  }, [selectedYear]);

  const handleYearChange = (event) => {
    const year = parseInt(event.target.value);
    setSelectedYear(year);
    fetchDateData(year);
  };

  const getYearName = (yearId) => {
    const year = availableYears.find((y) => y.id == yearId);
    return year ? year.name : "";
  };

  // Filter volunteers based on search term
  const filteredVolunteers = volunteerData.volunteersList.filter((volunteer) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      volunteer.name?.toLowerCase().includes(searchLower) ||
      volunteer.ITSNo?.toString().toLowerCase().includes(searchLower) ||
      volunteer.phone?.toString().toLowerCase().includes(searchLower) ||
      volunteer.date?.toLowerCase().includes(searchLower) ||
      volunteer.venue?.toLowerCase().includes(searchLower) ||
      volunteer.service?.toLowerCase().includes(searchLower) ||
      volunteer.status?.toLowerCase().includes(searchLower) ||
      volunteer.age?.toString().toLowerCase().includes(searchLower)
    );
  });

  // Download CSV function
  const downloadCSV = () => {
    const headers = ['Date', 'Venue', 'Service', 'ITS No', 'Name', 'Age', 'Phone', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredVolunteers.map(volunteer => [
        volunteer.date,
        volunteer.venue,
        volunteer.service,
        volunteer.ITSNo,
        `"${volunteer.name}"`, // Wrap name in quotes in case it contains commas
        volunteer.age || 'N/A',
        volunteer.phone,
        volunteer.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `volunteers_${getYearName(selectedYear)}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center p-6">
        <h2 className="text-2xl font-semibold text-center flex-grow">{getYearName(selectedYear)} - Dashboard</h2>

        {/* Year Dropdown */}
        <div className="flex items-center space-x-2">
          <label htmlFor="year-select" className="text-sm font-medium text-gray-700">
            Year:
          </label>
          <select
            id="year-select"
            value={selectedYear}
            onChange={handleYearChange}
            className="px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {availableYears.map(({ id, value }) => (
              <option key={id} value={id}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Total Volunteers */}
      <div className="bg-white grid grid-cols-2 md:grid-cols-4 gap-4 p-6 shadow-lg rounded-lg text-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">Total Volunteers Registered</h3>
          <p className="text-3xl font-bold text-blue-500">{volunteerData?.volunteersList?.length}</p>
        </div>
        <div>
          <h3 className="text-lg font-semibold">Unique Volunteers</h3>
          <p className="text-3xl font-bold text-blue-500">{volunteerData.total || 0}</p>
        </div>
        <div>
          <h3 className="text-lg font-semibold">Total Volunteers Attended</h3>
          <p className="text-3xl font-bold text-blue-500">{volunteerData.attendees || 0}</p>
        </div>
        <div>
          <h3 className="text-lg font-semibold">Unique Volunteers Attended</h3>
          <p className="text-3xl font-bold text-blue-500">{volunteerData.presentees || 0}</p>
        </div>
      </div>


      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Day-wise Volunteer Registration */}
        <div className="bg-white p-4 shadow-lg rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Day-wise Registration (Unique Volunteers: {volunteerData.uniqueVolunteersAcrossAllDays || 0})</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={volunteerData.daily}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                formatter={(value, name, props) => {
                  return [
                    `${value} volunteers`,
                    `${name}`
                  ];
                }}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Legend />
              <Bar dataKey="totalRegistrations" fill="#FFBB28" name="Total Registrations" />
              <Bar dataKey="newVolunteers" fill="#0088FE" name="New Volunteers" />
              <Bar dataKey="presentCount" fill="#00C49F" name="Khidmat Attended" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Service Distribution */}
        <div className="bg-white p-4 shadow-lg rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Khidmat Distribution (Unique Volunteers)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={volunteerData.serviceDist} dataKey="count" nameKey="service" cx="50%" cy="50%" minAngle={1} outerRadius={80}>
                {volunteerData.serviceDist.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => {
                  const totalVolunteers = volunteerData.serviceDist.reduce((sum, item) => sum + item.count, 0);
                  const percentage = ((value / totalVolunteers) * 100).toFixed(1);
                  return [
                    `${value} volunteers (${percentage}%)`,
                    name];
                }}
                labelFormatter={(label) => `Service: ${label}`}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Venue Distribution */}
      <div className="bg-white p-4 shadow-lg rounded-lg mt-6">
        <h3 className="text-lg font-semibold mb-2">Venue Distribution by Khidmat (Unique Volunteers: {volunteerData.uniqueVolunteersAcrossAllVenues || 0})</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={volunteerData.venueServiceDist}>
            <XAxis dataKey="venue" angle={-45} textAnchor="end" height={80} />
            <YAxis />
            <Tooltip
              formatter={(value, name, props) => {
                const data = props.payload;
                const venueTotal = volunteerData.allServices?.reduce((sum, service) => {
                  return sum + (data[service] || 0);
                }, 0) || 0;
                return [
                  `${value} volunteers`,
                  name
                ];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload.length > 0) {
                  const data = payload[0].payload;
                  const venueTotal = volunteerData.allServices?.reduce((sum, service) => {
                    return sum + (data[service] || 0);
                  }, 0) || 0;
                  return `${label} (Total: ${venueTotal} volunteers)`;
                }
                return label;
              }}
            />
            <Legend />
            {volunteerData.allServices?.map((service, index) => (
              <Bar
                key={service}
                dataKey={service}
                fill={COLORS[index % COLORS.length]}
                name={service}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Age-wise Distribution per Khidmat */}
      <div className="bg-white p-4 shadow-lg rounded-lg mt-6">
        <h3 className="text-lg font-semibold mb-2">Age-wise Distribution per Khidmat</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={volunteerData.ageServiceDist}>
            <XAxis dataKey="service" angle={-45} textAnchor="end" height={80} />
            <YAxis />
            <Tooltip
              formatter={(value, name, props) => {
                const data = props.payload;
                const serviceTotal = (data['11-14'] || 0) + (data['14-25'] || 0) + (data['25-45'] || 0) + (data['45+'] || 0);
                return [
                  `${value} volunteers`,
                  `${name}`
                ];
              }}
              labelFormatter={(label) => `Service: ${label}`}
            />
            <Legend />
            <Bar dataKey="11-14" fill="#0088FE" name="11-14 years" />
            <Bar dataKey="14-25" fill="#00C49F" name="14-25 years" />
            <Bar dataKey="25-45" fill="#FFBB28" name="25-45 years" />
            <Bar dataKey="45+" fill="#FF8042" name="45+ years" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Daily Volunteer List */}
      <div className="bg-white p-4 shadow-lg rounded-lg mt-6">
        <div className="flex flex-col space-y-4 mb-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
          <h3 className="text-lg font-semibold">Daily Volunteers: ({filteredVolunteers?.length || 0} of {volunteerData.volunteersList?.length || 0})</h3>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search by name, ITS, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:w-60 lg:w-80"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 focus:outline-none flex-shrink-0"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="max-h-80 overflow-auto border rounded-lg">
          <table className="w-full border-collapse min-w-max">
            <thead className="bg-gray-200 sticky top-0">
              <tr>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Date</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Venue</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Service</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">ITS No</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Name</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Age</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Phone</th>
                <th className="p-1 sm:p-2 border text-xs sm:text-sm">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredVolunteers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="p-4 text-center text-gray-500 text-xs sm:text-sm">
                    {searchTerm ? 'No volunteers found matching your search.' : 'No volunteer data available.'}
                  </td>
                </tr>
              ) : (
                filteredVolunteers.map((item, index) => (
                  <tr key={index} className="text-center border-b hover:bg-gray-50">
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm whitespace-nowrap">{item.date}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.venue}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.service}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.ITSNo}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.name}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.age || 'N/A'}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">{item.phone}</td>
                    <td className="p-1 sm:p-2 border text-xs sm:text-sm">
                      <span className={`px-1 sm:px-2 py-1 rounded text-xs font-semibold ${item.status === 'PRESENT' ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-800'
                        }`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Download button at bottom */}
        {allowDownload && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={downloadCSV}
              className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center space-x-2 transition-colors"
              title="Download filtered results as CSV"
            >
              <span>📥</span>
              <span>Download ({filteredVolunteers?.length || 0} records)</span>
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default Dashboard;
